"use client";
import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { Trash2, Plus, Edit } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ModalExclusao = ({ onConfirm, onCancel, loading }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4" aria-modal="true" role="dialog">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 animate-fade-in-scale">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Confirmar Exclusão</h2>
            <p className="text-slate-600">
                Tem a certeza que deseja excluir esta instrução? Esta ação não poderá ser desfeita.
            </p>
            <div className="mt-6 flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold"
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button
                    onClick={onConfirm}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                    disabled={loading}
                >
                    {loading ? 'Excluindo...' : 'Excluir'}
                </button>
            </div>
        </div>
    </div>
);


// --- Componente do Modal para Criar/Editar Instrução ---
const ModalInstrucao = ({ isOpen, onClose, onSave, instrucao, grupo, loading }) => {
    const [dadosFormulario, setDadosFormulario] = useState({
        titulo: '',
        descricao: '',
    });
    const [formError, setFormError] = useState('');
    const tituloRef = useRef(null);

    useEffect(() => {
        if (instrucao) {
            setDadosFormulario({
                titulo: instrucao.titulo || '',
                descricao: instrucao.descricao || '',
            });
        } else {
            setDadosFormulario({ titulo: '', descricao: '' });
        }
        setFormError('');
    }, [instrucao, isOpen]);

    useEffect(() => {
        if (isOpen && tituloRef.current) {
            tituloRef.current.focus();
        }
    }, [isOpen]);

    const handleChange = (e) => {
        setDadosFormulario({ ...dadosFormulario, [e.target.name]: e.target.value });
        if (e.target.name === 'titulo') {
            if (!e.target.value.trim()) {
                setFormError('O título não pode estar vazio.');
            } else {
                setFormError('');
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!grupo) {
            setFormError('Erro: não foi possível identificar o grupo.');
            return;
        }
        if (!dadosFormulario.titulo.trim()) {
            setFormError('O título não pode estar vazio.');
            return;
        }
        const payload = {
            ...dadosFormulario,
            paroquia_id: grupo.paroquia_id,
            comunidade_id: grupo.comunidade_id,
            grupo_id: grupo.grupo_id,
        };
        onSave(payload, instrucao?.id);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40 p-4" aria-modal="true" role="dialog">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md animate-fade-in-scale">
                <h2 className="text-xl font-bold mb-4">
                    {instrucao ? 'Editar Instrução' : 'Nova Instrução'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="text"
                        name="titulo"
                        placeholder="Título"
                        value={dadosFormulario.titulo}
                        onChange={handleChange}
                        className="w-full p-3 border border-slate-300 rounded-lg"
                        required
                        ref={tituloRef}
                        disabled={loading}
                    />
                    <textarea
                        name="descricao"
                        placeholder="Descrição da Instrução"
                        value={dadosFormulario.descricao}
                        onChange={handleChange}
                        className="w-full p-3 border border-slate-300 rounded-lg"
                        rows="6"
                        disabled={loading}
                    ></textarea>
                    {formError && <div className="text-red-500 mt-2">{formError}</div>}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={() => { setFormError(''); onClose(); }} className="px-5 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-semibold" disabled={loading}>
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-5 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700"
                            disabled={!!formError || loading}
                        >
                            {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente Principal ---
export default function GerenciadorInstrucoes({ grupo, usuarioPainel, token }) {
    const [instrucoes, setInstrucoes] = useState([]);
    const [instrucaoSelecionada, setInstrucaoSelecionada] = useState(null);
    const [modalFormAberto, setModalFormAberto] = useState(false);
    const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
    const [idParaExcluir, setIdParaExcluir] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loadingSalvar, setLoadingSalvar] = useState(false);
    const [loadingExcluir, setLoadingExcluir] = useState(false);

    const carregarDados = useCallback(async (isBackground = false) => {
        if (!token || !grupo?.grupo_id) return;
        try {
            if (!isBackground) setLoading(true);
            setError('');
            const response = await api.get(`/instrucoes/grupo/${grupo.grupo_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInstrucoes(response.data || []);
        } catch (err) {
            setError('Erro ao carregar instruções do grupo.');
            setInstrucoes([]);
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, [token, grupo]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const abrirModalEdicao = (instrucao) => {
        setInstrucaoSelecionada(instrucao);
        setModalFormAberto(true);
        setError('');
    };

    const abrirModalCriacao = () => {
        setInstrucaoSelecionada(null);
        setModalFormAberto(true);
        setError('');
    };

    const fecharModalForm = () => {
        setModalFormAberto(false);
        setInstrucaoSelecionada(null);
        setError('');
    };

    const salvarInstrucao = async (dados, id) => {
        setLoadingSalvar(true);
        try {
            let savedInstrucao;
            if (id) {
                const res = await api.put(`/instrucoes-admin/${id}`, dados, { headers: { Authorization: `Bearer ${token}` } });
                savedInstrucao = res.data;
                setInstrucoes(prev => prev.map(inst => inst.id === id ? { ...inst, ...savedInstrucao } : inst));
            } else {
                const res = await api.post('/instrucoes-admin', dados, { headers: { Authorization: `Bearer ${token}` } });
                savedInstrucao = res.data;
                setInstrucoes(prev => [savedInstrucao, ...prev]);
            }
            fecharModalForm();
            toast.success('Instrução salva com sucesso!');
            carregarDados(true);
        } catch (err) {
            setError('Erro ao salvar instrução.');
            toast.error('Erro ao salvar instrução.');
        } finally {
            setLoadingSalvar(false);
        }
    };

    const confirmarExclusao = (id) => {
        setIdParaExcluir(id);
        setModalExcluirAberto(true);
        setError('');
    };

    const excluirConfirmado = async () => {
        setLoadingExcluir(true);
        try {
            await api.delete(`/instrucoes-admin/${idParaExcluir}`, { headers: { Authorization: `Bearer ${token}` } });
            setInstrucoes(prev => prev.filter(inst => inst.id !== idParaExcluir));
            setModalExcluirAberto(false);
            setIdParaExcluir(null);
            toast.success('Instrução excluída com sucesso!');
            carregarDados(true);
        } catch (err) {
            setError('Erro ao excluir instrução.');
            toast.error('Erro ao excluir instrução.');
        } finally {
            setLoadingExcluir(false);
        }
    };

    // Permissão: só coordenador OU admin pode acessar
    const roles = (usuarioPainel?.roles || []).map(r => r.toLowerCase());
    if (!roles.includes('coordenador') && !roles.includes('admin')) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
                <div className="bg-white p-8 rounded-xl shadow text-center max-w-md">
                    <h2 className="text-2xl font-bold text-red-600 mb-2">Acesso negado</h2>
                    <p className="text-gray-700 mb-6">Você não tem permissão para acessar este painel.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="font-sans">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <style>{`.animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; } @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Instruções do Grupo</h1>
                <button
                    onClick={abrirModalCriacao}
                    className="mt-4 sm:mt-0 bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:bg-indigo-300"
                    disabled={!grupo || !grupo.grupo_id || loadingSalvar || loadingExcluir}
                >
                    <Plus size={20} />
                    {loadingSalvar ? 'Salvando...' : loadingExcluir ? 'Excluindo...' : 'Adicionar Instrução'}
                </button>
            </div>
            {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4 text-center font-semibold">
                    {error}
                </div>
            )}
            {loading ? <p className="text-center text-slate-500">A carregar...</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {instrucoes.map((instrucao) => (
                        <div key={instrucao.id} className="bg-white p-6 rounded-xl shadow-md border border-slate-200 flex flex-col justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">{instrucao.titulo}</h2>
                                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{instrucao.descricao}</p>
                            </div>
                            <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end gap-2">
                                <button
                                    onClick={() => abrirModalEdicao(instrucao)}
                                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                                    aria-label="Editar"
                                    disabled={loadingSalvar || loadingExcluir}
                                >
                                    <Edit size={18} />
                                </button>
                                <button
                                    onClick={() => confirmarExclusao(instrucao.id)}
                                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                                    aria-label="Excluir"
                                    disabled={loadingSalvar || loadingExcluir}
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {instrucoes.length === 0 && !loading && !error && (
                <div className="text-center py-16 px-6 bg-slate-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-slate-700">Nenhuma instrução encontrada</h3>
                    <p className="text-slate-500 mt-2">Clique em "Adicionar Instrução" para começar.</p>
                </div>
            )}
            <ModalInstrucao 
                isOpen={modalFormAberto} 
                onClose={fecharModalForm}
                onSave={salvarInstrucao}
                instrucao={instrucaoSelecionada}
                grupo={grupo}
                loading={loadingSalvar}
            />
            {modalExcluirAberto && (
                <ModalExclusao 
                    onConfirm={excluirConfirmado}
                    onCancel={() => setModalExcluirAberto(false)}
                    loading={loadingExcluir}
                />
            )}
        </div>
    );
}
